import styled from 'styled-components';
import { motion } from 'framer-motion';

const ResultsContainer = styled.div`
  min-height: 100vh;
  padding: 2rem;
  position: relative;
`;

const ResultsContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const NoResultsMessage = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: rgba(18, 18, 18, 0.7);
  border-radius: var(--border-radius);
  backdrop-filter: blur(10px);
  margin-top: 2rem;
  
  h2 {
    margin-bottom: 1rem;
    color: var(--text-primary);
  }
  
  p {
    color: var(--text-secondary);
    margin-bottom: 2rem;
  }
`;

const RetakeButton = styled(motion.button)`
  background: var(--accent-solid);
  color: #0C0F14;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.5px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.4);
  }
`;

const ScoreCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  width: 100%;
  margin-bottom: 3rem;
`;

const DetailedCard = styled.div`
  background-color: rgba(18, 18, 28, 0.75);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      var(--type-accent, var(--primary-color)),
      var(--type-accent-dark, var(--secondary-color))
    );
  }
`;

const SectionTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 1.35rem;
  margin-bottom: 1.25rem;
  color: var(--text-primary);
  font-weight: 800;
  letter-spacing: -0.02em;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const ActionPlan = styled.div`
  margin-top: 1rem;
  line-height: 1.6;
  color: var(--text-secondary);
  font-size: 1.1rem;
  
  strong {
    color: var(--primary-color);
    font-weight: 600;
  }
`;

const OperatingCard = styled(DetailedCard)`
  width: 100%;
  max-width: 900px;
`;

const List = styled.ul`
  color: var(--text-secondary);
  line-height: 1.6;
  padding-left: 1.25rem;
  margin: 0.75rem 0 0;
`;

const OrderedList = styled.ol`
  color: var(--text-secondary);
  line-height: 1.6;
  padding-left: 1.25rem;
  margin: 0.75rem 0 0;
`;

const SmallMeta = styled.p`
  margin-top: 0.75rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.95rem;
`;

const HistoryCard = styled(OperatingCard)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.82) 0%,
    rgba(18, 18, 18, 0.7) 55%,
    rgba(255, 255, 255, 0.05) 100%
  );
`;

const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin-top: 1rem;
`;

const HistoryRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.6rem;
  padding: 0.9rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (min-width: 768px) {
    grid-template-columns: 1fr auto;
    align-items: center;
  }
`;

const HistoryRowMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.95rem;
`;

const HistoryRowTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
  color: var(--text-primary);
  font-weight: 700;
`;

const HistoryBadge = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  background: rgba(var(--primary-rgb), 0.18);
  border: 1px solid rgba(var(--primary-rgb), 0.25);
  color: rgba(255, 255, 255, 0.88);
`;

const HistoryNameInput = styled.input`
  width: 100%;
  max-width: 340px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  color: var(--text-primary);
  outline: none;

  &:focus-visible {
    border-color: rgba(var(--primary-rgb), 0.35);
    box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.15);
  }
`;

const HistoryActionRow = styled.div`
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: flex-start;

  @media (min-width: 768px) {
    justify-content: flex-end;
  }
`;

const HistoryButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: var(--text-primary);
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.65rem 0.9rem;
  border-radius: 12px;
  cursor: pointer;
  white-space: nowrap;
`;

const CheckInCard = styled(OperatingCard)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.8) 0%,
    rgba(18, 18, 18, 0.7) 50%,
    rgba(var(--primary-rgb), 0.08) 100%
  );
  border: 1px solid rgba(var(--primary-rgb), 0.18);
`;

const CheckInRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const CheckInMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  color: rgba(255, 255, 255, 0.65);
`;

const CheckInButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent-solid);
  color: #0C0F14;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.85rem 1.1rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  white-space: nowrap;
`;

const ReminderButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1rem;
  padding: 0.85rem 1.1rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  white-space: nowrap;
`;

const ExportButton = styled(ReminderButton)`
  background: rgba(17, 17, 17, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

const DangerButton = styled(ReminderButton)`
  background: rgba(248, 113, 113, 0.12);
  border: 1px solid rgba(248, 113, 113, 0.35);
  color: #fecaca;
`;

const EmailConsentLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin-top: 1rem;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--text-secondary);
  cursor: pointer;

  input {
    margin-top: 0.2rem;
    accent-color: var(--primary-color);
  }

  a {
    color: var(--primary-light);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const EmailCaptureCard = styled(OperatingCard)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.82) 0%,
    rgba(18, 18, 18, 0.7) 50%,
    rgba(37, 65, 178, 0.08) 100%
  );
  border: 1px solid rgba(37, 65, 178, 0.18);
`;

const ConfidenceCard = styled(OperatingCard)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.82) 0%,
    rgba(18, 18, 18, 0.7) 50%,
    rgba(255, 255, 255, 0.05) 100%
  );
`;

const BackupCard = styled(OperatingCard)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.82) 0%,
    rgba(18, 18, 18, 0.7) 50%,
    rgba(255, 255, 255, 0.05) 100%
  );
`;

const BackupRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 1rem;
`;

const BackupMeta = styled.div`
  margin-top: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const BackupNotice = styled.p`
  margin-top: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  font-size: 0.95rem;
`;

const ArchetypeCard = styled(OperatingCard)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.82) 0%,
    rgba(18, 18, 18, 0.7) 50%,
    rgba(var(--primary-rgb), 0.06) 100%
  );
  border: 1px solid rgba(var(--primary-rgb), 0.14);
`;

const EmailRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
`;

const EmailInput = styled.input`
  flex: 1;
  min-width: 220px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--border-radius);
  color: var(--text-primary);
  padding: 0.9rem 1rem;
  font-size: 1rem;

  &:focus-visible {
    outline: 2px solid rgba(var(--primary-rgb), 0.65);
    outline-offset: 2px;
    border-color: rgba(37, 65, 178, 0.6);
  }
`;

const EmailButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, rgba(37, 65, 178, 1), rgba(var(--primary-rgb), 1));
  color: white;
  font-weight: 600;
  font-size: 1rem;
  padding: 0.9rem 1.1rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HelperText = styled.p`
  margin-top: 0.75rem;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1.5;
  font-size: 0.95rem;
`;

/* Compatibility Section */
const CompatibilitySection = styled(DetailedCard)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.7) 0%,
    rgba(18, 18, 18, 0.8) 50%,
    rgba(var(--primary-rgb), 0.1) 100%
  );
  border: 1px solid rgba(var(--primary-rgb), 0.2);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const CompatibilityDescription = styled.p`
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  font-size: 1.05rem;
  line-height: 1.6;
  max-width: 700px;
`;

const CompatibilityTitle = styled(SectionTitle)`
  background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
  border-bottom: none;
  position: relative;
  font-weight: 700;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, var(--primary-color), transparent);
  }
`;

// Add styled component for the profile breakdown section
const ProfileBreakdownSection = styled(DetailedCard)`
  background: linear-gradient(
    to bottom right,
    rgba(18, 18, 18, 0.8) 0%,
    rgba(18, 18, 18, 0.7) 50%,
    rgba(37, 65, 178, 0.1) 100%
  );
  border: 1px solid rgba(37, 65, 178, 0.2);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const ProfileBreakdownTitle = styled(SectionTitle)`
  background: linear-gradient(90deg, var(--secondary-color), var(--primary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
  border-bottom: none;
  position: relative;
  font-weight: 700;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(90deg, var(--secondary-color), transparent);
  }
`;

export {
  ResultsContainer,
  ResultsContent,
  NoResultsMessage,
  RetakeButton,
  ScoreCardsContainer,
  DetailedCard,
  SectionTitle,
  ActionPlan,
  OperatingCard,
  List,
  OrderedList,
  SmallMeta,
  HistoryCard,
  HistoryList,
  HistoryRow,
  HistoryRowMeta,
  HistoryRowTitle,
  HistoryBadge,
  HistoryNameInput,
  HistoryActionRow,
  HistoryButton,
  CheckInCard,
  CheckInRow,
  CheckInMeta,
  CheckInButton,
  ReminderButton,
  ExportButton,
  DangerButton,
  EmailConsentLabel,
  EmailCaptureCard,
  ConfidenceCard,
  BackupCard,
  BackupRow,
  BackupMeta,
  BackupNotice,
  ArchetypeCard,
  EmailRow,
  EmailInput,
  EmailButton,
  HelperText,
  CompatibilitySection,
  CompatibilityDescription,
  CompatibilityTitle,
  ProfileBreakdownSection,
  ProfileBreakdownTitle,
};
